import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "@tanstack/react-router";
import { formatDistanceToNow } from "date-fns";
import { ar as arLocale, enUS as enLocale } from "date-fns/locale";
import { AlertTriangle, Bell, ReceiptText, Settings, Wrench } from "lucide-react";
import {
  cn,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Separator,
  Skeleton,
} from "@nasam/ui";
import {
  useMarkAllNotificationsReadMutation,
  useMarkNotificationReadMutation,
  useNotificationsQuery,
  useNotificationsSummaryQuery,
} from "../application";
import type {
  AppNotification,
  NotificationCategory,
  NotificationsRepository,
} from "../domain";
import { usePushRegistration } from "../push/usePushRegistration";

const TOP_BAR_ICON_BUTTON =
  "relative flex size-9 items-center justify-center rounded-md text-text transition-colors duration-150 ease-emphasized hover:bg-neutral-100 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary";

const LINK_BUTTON =
  "text-body-caption-m-bold text-primary transition-opacity hover:opacity-80 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary rounded-sm";

const CATEGORY_ICONS: Record<NotificationCategory, typeof AlertTriangle> = {
  ALERT: AlertTriangle,
  SYSTEM: Settings,
  SUBSCRIPTION: ReceiptText,
  MAINTENANCE: Wrench,
};

export interface NotificationsPanelProps {
  readonly repository: NotificationsRepository;
}

export function NotificationsPanel({ repository }: NotificationsPanelProps) {
  const { t, i18n } = useTranslation("notifications");
  const navigate = useNavigate();
  const isAr = i18n.language.startsWith("ar");
  const [open, setOpen] = useState(false);

  const summary = useNotificationsSummaryQuery(repository);
  const list = useNotificationsQuery(repository, { limit: 20 }, open);
  const markAsRead = useMarkNotificationReadMutation(repository);
  const markAllAsRead = useMarkAllNotificationsReadMutation(repository);
  const { permissionState, enable } = usePushRegistration(repository, isAr);

  const unreadCount = summary.data?.unreadCount ?? 0;
  const rows = list.data?.data ?? [];
  const bellLabel =
    unreadCount > 0
      ? t("bellLabelWithCount", { count: unreadCount })
      : t("common:notifications");

  const handleRowClick = (notification: AppNotification) => {
    if (!notification.isRead) {
      markAsRead.mutate(notification.id);
    }
    if (notification.linkPath !== null) {
      setOpen(false);
      void navigate({ to: notification.linkPath });
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid="top-bar-notifications"
          aria-label={bellLabel}
          className={TOP_BAR_ICON_BUTTON}
        >
          <Bell className="size-5 shrink-0" />
          {unreadCount > 0 && (
            <span
              aria-hidden="true"
              className="absolute end-1 top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-fail-text px-1 text-[0.625rem] font-bold leading-none text-white"
            >
              {unreadCount > 9 ? "9+" : unreadCount}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0">
        <div className="flex items-center justify-between gap-2 p-3">
          <span className="text-body-m-b text-text">{t("title")}</span>
          {unreadCount > 0 && (
            <button
              type="button"
              onClick={() => markAllAsRead.mutate()}
              className={LINK_BUTTON}
            >
              {t("markAllAsRead")}
            </button>
          )}
        </div>
        {permissionState === "default" && (
          <div className="px-3 pb-3">
            <button
              type="button"
              onClick={() => void enable()}
              className={LINK_BUTTON}
            >
              {t("enablePush")}
            </button>
          </div>
        )}
        <Separator />
        <div className="max-h-[70vh] overflow-y-auto">
          {list.isLoading ? (
            <div className="flex flex-col gap-3 p-3" aria-busy="true">
              <span className="sr-only">{t("loading")}</span>
              {[0, 1, 2].map((key) => (
                <div key={key} className="flex gap-3">
                  <Skeleton variant="circle" />
                  <div className="flex flex-1 flex-col gap-2">
                    <Skeleton variant="text" className="w-3/4" />
                    <Skeleton variant="text" className="w-1/2" />
                  </div>
                </div>
              ))}
            </div>
          ) : list.isError ? (
            <p className="p-4 text-center text-body-caption-m text-fail-text">
              {t("error")}
            </p>
          ) : rows.length === 0 ? (
            <p className="p-4 text-center text-body-caption-m text-text-placeholder">
              {t("empty")}
            </p>
          ) : (
            <ul className="flex flex-col">
              {rows.map((notification) => {
                const Icon = CATEGORY_ICONS[notification.category];
                return (
                  <li key={notification.id}>
                    <button
                      type="button"
                      onClick={() => handleRowClick(notification)}
                      className={cn(
                        "flex w-full items-start gap-3 p-3 text-start transition-colors hover:bg-neutral-100 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary",
                        !notification.isRead && "bg-primary/5",
                      )}
                    >
                      <Icon className="mt-0.5 size-4 shrink-0 text-text-placeholder" />
                      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                        <span className="sr-only">
                          {t(`category.${notification.category.toLowerCase()}`)}
                        </span>
                        <span className="text-body-caption-m-bold text-text">
                          {isAr ? notification.titleAr : notification.titleEn}
                        </span>
                        <span className="line-clamp-2 text-body-caption-m text-text-placeholder">
                          {isAr ? notification.bodyAr : notification.bodyEn}
                        </span>
                        <span className="text-body-caption-m text-text-placeholder">
                          {formatDistanceToNow(new Date(notification.createdAt), {
                            addSuffix: true,
                            locale: isAr ? arLocale : enLocale,
                          })}
                        </span>
                      </span>
                      {!notification.isRead && (
                        <span
                          aria-hidden="true"
                          className="mt-1.5 size-2 shrink-0 rounded-full bg-primary"
                        />
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
